import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';

import React, { useContext, useEffect, useRef, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { ActionBar } from '_lib/components/ActionBar/ActionBar';
import { WarningAlert } from '_lib/components/Alert/Alert';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Tab } from '_lib/components/Tab/Tab';
import { TabPanel } from '_lib/components/TabPanel/TabPanel';
import { Tabs } from '_lib/components/Tabs/Tabs';
import { Typography } from '_lib/components/Typography/Typography';
import { AccountUserTabs } from '_lib/featureComponents/AccountUser/AccountUserTabs';
import { AuthContext } from '_lib/featureComponents/auth/authContext';
import { NavigationBlocker } from '_lib/featureComponents/NavigationBlocker/NavigationBlocker';
import { RouteErrorBoundary } from '_lib/featureComponents/Routing/RouteErrorBoundary';

import { normalizers, useQueryParams } from '_lib/hooks/useQueryParams';
import { logDebug } from '_lib/utils/logging';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import { CompanyConfigBilling } from './CompanyTabs/CompanyConfigBilling';
import { CompanyConfigDetail } from './CompanyTabs/CompanyConfigDetail';
import { CompanyConfigNotes } from './CompanyTabs/CompanyConfigNotes';
import { CompanyConfigSettings } from './CompanyTabs/CompanyConfigSettings';
import { CompanyConfigZipZones } from './CompanyTabs/CompanyConfigZipZones';
import { CompanyContacts } from './CompanyTabs/CompanyContacts';
import { CompanyConfigDataProvider } from './CompanyTabs/DataProvider/CompanyConfigDataProvider';

enum CompanyUpdateTabs {
  DETAILS = 'DETAILS',
  CONTACTS = 'CONTACTS',
  NOTES = 'NOTES',
  ZIP_ZONES = 'ZIP/ZONES',
  SETTINGS = 'SETTINGS',
  DATA_PROVIDER = 'DATA PROVIDER',
  BILLING = 'BILLING',
  USERS = 'USERS'
}

export type TabChangeObj = {
  onSave: () => Promise<void>;
  isValid: boolean;
  isDirty: boolean;
  isTouched: boolean;
};

export type TabChangeRef = { [key in CompanyUpdateTabs]: TabChangeObj };

export const CompanyUpdate = ({
  companyId,
  setCompanyName
}: {
  companyId: string;
  setCompanyName: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const { params, setParam } = useQueryParams({
    currentTab: {
      defaultValue: CompanyUpdateTabs.DETAILS,
      normalize: normalizers.enum(Object.values(CompanyUpdateTabs))
    }
  });

  const [tabIndex, setTabIndex] = useState(params.currentTab);

  const tabChangeRef = useRef<TabChangeRef>({
    [CompanyUpdateTabs.DETAILS]: { onSave: async () => {}, isValid: true, isDirty: false, isTouched: false },
    [CompanyUpdateTabs.CONTACTS]: { onSave: async () => {}, isValid: true, isDirty: false, isTouched: false },
    [CompanyUpdateTabs.NOTES]: { onSave: async () => {}, isValid: true, isDirty: false, isTouched: false },
    [CompanyUpdateTabs.ZIP_ZONES]: { onSave: async () => {}, isValid: true, isDirty: false, isTouched: false },
    [CompanyUpdateTabs.SETTINGS]: { onSave: async () => {}, isValid: true, isDirty: false, isTouched: false },
    [CompanyUpdateTabs.DATA_PROVIDER]: { onSave: async () => {}, isValid: true, isDirty: false, isTouched: false },
    [CompanyUpdateTabs.BILLING]: { onSave: async () => {}, isValid: true, isDirty: false, isTouched: false },
    [CompanyUpdateTabs.USERS]: { onSave: async () => {}, isValid: true, isDirty: false, isTouched: false }
  });

  const { enqueueSnackbar } = useOurSnackbar();

  const api = useContext(ApiContext);

  const companyDetailQuery = api.companies.useGetById({ id: companyId });
  useEffect(() => {
    if (companyDetailQuery.data?.name) {
      setCompanyName(companyDetailQuery.data.name);
    } else {
      setCompanyName('');
    }
  }, [companyDetailQuery.data?.name, setCompanyName]);

  const accountDetailQuery = api.accounts.useGetById(
    companyDetailQuery.data?.accountId ? { id: companyDetailQuery.data.accountId } : { skipQuery: true }
  );
  const companyReactivateMutation = api.companies.useReactivate();

  const appConfigQuery = api.appConfig.useAppConfigQuery();

  const { authUserCacheResource, callCenterCacheResource } = useContext(AuthContext);
  const authUserQuery = authUserCacheResource.useCacheQuery();
  const callCenterQuery = callCenterCacheResource.useCacheQuery();

  if (companyDetailQuery.error || authUserQuery.error || accountDetailQuery.error || callCenterQuery.error) {
    return <RouteErrorBoundary disableAppBar errorTitle='Error Fetching Company' />;
  }

  const isFetchingOrPending =
    companyDetailQuery.isFetching ||
    authUserQuery.isFetching ||
    accountDetailQuery.isFetching ||
    callCenterQuery.isFetching ||
    companyReactivateMutation.isPending;

  if (!isFetchingOrPending && !companyDetailQuery.data) {
    return <RouteErrorBoundary errorTitle='Company Not Found' />;
  }

  return (
    <FlexBox sx={{ flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden', position: 'relative' }}>
      <NavigationBlocker
        getBlockCondition={() => tabChangeRef.current[tabIndex].isDirty && tabChangeRef.current[tabIndex].isTouched}
      />
      <BackdropSpinner open={isFetchingOrPending} />
      {companyDetailQuery?.data && companyDetailQuery.data.isActive === false && (
        <>
          <ActionBar
            actions={
              <Button
                disableFocusRipple
                text={'Reactivate'}
                variant='text'
                onClick={async () => {
                  try {
                    await companyReactivateMutation.reactivate({ id: companyDetailQuery.data.id });
                    enqueueSnackbar('Company reactivated successfully');
                  } catch (error) {
                    logDebug('Error reactivating company', { error });
                    enqueueSnackbar('Failed to reactivate company.', { variant: 'error' });
                  }
                }}
                startIcon={<SettingsBackupRestoreIcon />}
                sx={{ textTransform: 'none' }}
                disabled={companyReactivateMutation.isPending}
              />
            }
          />
          <WarningAlert>
            <Typography text='Company is inactive' />
          </WarningAlert>
          <Divider />
        </>
      )}
      <Tabs
        value={tabIndex}
        variant='scrollable'
        scrollButtons='auto'
        allowScrollButtonsMobile
        onChange={async (_event, newValue) => {
          if (
            (tabChangeRef.current[tabIndex].isDirty && tabChangeRef.current[tabIndex].isTouched) ||
            tabChangeRef.current[tabIndex].isValid === false // We want to trigger 'save' if the pre-loaded form is invalid as well... TODO: add guardrails?
          ) {
            await tabChangeRef.current[tabIndex].onSave();
          }
          if (tabChangeRef.current[tabIndex].isValid === false) return;

          setParam('currentTab', newValue);
          setTabIndex(newValue);
        }}>
        <Tab value={CompanyUpdateTabs.DETAILS} label={CompanyUpdateTabs.DETAILS} />
        <Tab value={CompanyUpdateTabs.CONTACTS} label={CompanyUpdateTabs.CONTACTS} />
        <Tab value={CompanyUpdateTabs.NOTES} label={CompanyUpdateTabs.NOTES} />
        <Tab value={CompanyUpdateTabs.ZIP_ZONES} label={CompanyUpdateTabs.ZIP_ZONES} />
        {authUserQuery?.data?.settings?.callPilot?.companiesSettings && (
          <Tab value={CompanyUpdateTabs.SETTINGS} label={CompanyUpdateTabs.SETTINGS} />
        )}
        {authUserQuery?.data?.settings?.callPilot?.companiesSettings && (
          <Tab value={CompanyUpdateTabs.DATA_PROVIDER} label={CompanyUpdateTabs.DATA_PROVIDER} />
        )}
        {authUserQuery?.data?.settings?.callPilot?.companiesBilling && (
          <Tab value={CompanyUpdateTabs.BILLING} label={CompanyUpdateTabs.BILLING} />
        )}
        {authUserQuery?.data?.settings?.callPilot?.companiesSettings && companyDetailQuery?.data?.isActive && (
          <Tab value={CompanyUpdateTabs.USERS} label={CompanyUpdateTabs.USERS} />
        )}
      </Tabs>
      <Divider />
      {companyDetailQuery.data && authUserQuery.data && accountDetailQuery.data && callCenterQuery.data && (
        <>
          <TabPanel currentIndex={tabIndex} index={CompanyUpdateTabs.DETAILS} sx={{ overflow: 'hidden' }}>
            <CompanyConfigDetail
              company={companyDetailQuery.data}
              authUser={authUserQuery.data}
              nationalNumberGroups={appConfigQuery.data?.nationalNumberGroups}
              account={accountDetailQuery.data}
              tabChangeObj={tabChangeRef.current[CompanyUpdateTabs.DETAILS]}
            />
          </TabPanel>
          <TabPanel currentIndex={tabIndex} index={CompanyUpdateTabs.CONTACTS} sx={{ overflow: 'hidden' }}>
            <CompanyContacts
              company={companyDetailQuery.data}
              authUser={authUserQuery.data}
              tabChangeObj={tabChangeRef.current[CompanyUpdateTabs.CONTACTS]}
            />
          </TabPanel>
          <TabPanel currentIndex={tabIndex} index={CompanyUpdateTabs.NOTES} sx={{ overflow: 'hidden' }}>
            <CompanyConfigNotes
              company={companyDetailQuery.data}
              authUser={authUserQuery.data}
              tabChangeObj={tabChangeRef.current[CompanyUpdateTabs.NOTES]}
            />
          </TabPanel>
          <TabPanel currentIndex={tabIndex} index={CompanyUpdateTabs.ZIP_ZONES} sx={{ overflow: 'hidden' }}>
            <CompanyConfigZipZones
              company={companyDetailQuery.data}
              authUser={authUserQuery.data}
              tabChangeObj={tabChangeRef.current[CompanyUpdateTabs.ZIP_ZONES]}
              isTabSelected={tabIndex === CompanyUpdateTabs.ZIP_ZONES}
            />
          </TabPanel>
          {authUserQuery?.data?.settings?.callPilot?.companiesSettings && (
            <TabPanel currentIndex={tabIndex} index={CompanyUpdateTabs.SETTINGS} sx={{ overflow: 'hidden' }}>
              <CompanyConfigSettings
                company={companyDetailQuery.data}
                authUser={authUserQuery.data}
                callCenter={callCenterQuery.data}
                tabChangeObj={tabChangeRef.current[CompanyUpdateTabs.SETTINGS]}
              />
            </TabPanel>
          )}
          {authUserQuery?.data?.settings?.callPilot?.companiesSettings && (
            <TabPanel currentIndex={tabIndex} index={CompanyUpdateTabs.DATA_PROVIDER} sx={{ overflow: 'hidden' }}>
              <CompanyConfigDataProvider
                company={companyDetailQuery.data}
                authUser={authUserQuery.data}
                tabChangeObj={tabChangeRef.current[CompanyUpdateTabs.DATA_PROVIDER]}
              />
            </TabPanel>
          )}
          {authUserQuery?.data?.settings?.callPilot?.companiesBilling && (
            <TabPanel currentIndex={tabIndex} index={CompanyUpdateTabs.BILLING} sx={{ overflow: 'hidden' }}>
              <CompanyConfigBilling
                company={companyDetailQuery.data}
                authUser={authUserQuery.data}
                tabChangeObj={tabChangeRef.current[CompanyUpdateTabs.BILLING]}
              />
            </TabPanel>
          )}
          {authUserQuery?.data?.settings?.callPilot?.companiesSettings && companyDetailQuery?.data.isActive && (
            <TabPanel currentIndex={tabIndex} index={CompanyUpdateTabs.USERS} sx={{ overflow: 'hidden' }}>
              <AccountUserTabs
                accountId={companyDetailQuery.data.accountId}
                account={accountDetailQuery.data}
                renderWithActionBar
              />
            </TabPanel>
          )}
        </>
      )}
    </FlexBox>
  );
};
